
import React, { useEffect, useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import useScrollToTop from "@/hooks/useScrollToTop";
import { Helmet } from "react-helmet-async";

const Terms = () => {
  useScrollToTop();
  const [activeVersion, setActiveVersion] = useState<"current" | "previous">("current");
  const isPreviousVersion = activeVersion === "previous";
  const hasOlderPreviousVersion = false;
  const publishedDate = isPreviousVersion ? "APRIL 15, 2024" : "AUGUST 1, 2026";
  const effectiveDate = isPreviousVersion ? "APRIL 15, 2024" : "AUGUST 1, 2026";

  useEffect(() => {
    document.title = "Terms and Conditions | YouAux";
  }, []);

  return (
    <>
    <Helmet>
        <title>Terms and Conditions | YouAux</title>
        <meta name="description" content="These Terms of Service contain the terms and conditions that govern your use of the Site." />
        <link rel="canonical" href="https://youaux.com/terms-and-conditions" />
      </Helmet>
      <Navbar />
      <main className="ophira-bg bg-ophira-dark min-h-screen">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-4xl mx-auto bg-ophira-darkgray/30 p-8 rounded-lg shadow-xl">
            <div className="mb-10 text-center">
              <p className="text-white/70 text-sm font-medium uppercase tracking-wider">
                Published: {publishedDate}
              </p>
              <h1 className="text-4xl md:text-5xl font-bold mt-4 mb-4">TERMS & CONDITION</h1>
              {isPreviousVersion && (
                <>
                  <p className="text-white/60 text-base md:text-md mt-2">
                    This is an archived page of our April 2024 terms and conditions.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveVersion("current")}
                    className="mt-6 px-6 py-2.5 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors"
                  >
                    See latest terms
                  </button>
                </>
              )}
              <p className="text-ophira-yellow text-sm font-medium uppercase tracking-wider mt-8 text-left">
                Effective: {effectiveDate}
                {!isPreviousVersion && (
                  <>
                    {" "}(
                    <button
                      type="button"
                      onClick={() => setActiveVersion("previous")}
                      className="text-gray-200 underline underline-offset-2 hover:text-ophira-yellow transition-colors"
                    >
                      Previous version
                    </button>
                    )
                  </>
                )}
                {isPreviousVersion && hasOlderPreviousVersion && (
                  <>
                    {" "}(
                    <button
                      type="button"
                      onClick={() => setActiveVersion("previous")}
                      className="text-gray-200 underline underline-offset-2 hover:text-ophira-yellow transition-colors"
                    >
                      Previous version
                    </button>
                    )
                  </>
                )}
              </p>
            </div>

            <div className="prose prose-invert prose-lg max-w-none">
              <p className="text-white/50">
                Welcome to the <span className="text-ophira-yellow">YouAux Entertainment</span> website which is located at <a href="https://youaux.com/" target="_blank">www.youaux.com</a> (the "Site"). The following Terms of Service
                ("Terms") contain the terms and conditions that govern your use of the Site. Any use of the services provided by YouAux Music
                shall be subject to the <span className="text-ophira-yellow">YouAux Digital Distribution Services Agreement</span>. Access and use of the Site constitutes your
                agreement to and acceptance of all the Terms.
              </p>

              <p className="text-white/50 mt-6">
                YouAux Entertainment Private Limited ("YouAux" or "we") provides the Services (as defined below) to the User ("you" or
                "your") through its web platform located at the Site. By accepting these Terms or by accessing or using the Site and the
                Services, you acknowledge that you have read, understood, and agree to be bound by these Terms, including the
                YouAux Privacy Policy, the YouAux Distribution Agreement and any other legal terms & conditions as listed by
                YouAux on the Site. If you are entering into this agreement on behalf of a company, business or other legal entity, you
                represent that you have the authority to bind such entity and its affiliates to these Terms, in which case the terms "you" or
                "your" shall refer to such entity and its affiliates. If you do not have such authority, or if you do not agree with these Terms,
                you shall discontinue all use of the Site and the Services and may not use the Service.
              </p>

              <p className="text-white/50 mt-6">
                YouAux reserves the right to modify or update these Terms from time to time by providing prior notice either by emailing
                the email address associated with your account or by posting a notice on the Platform (as defined below). You can review
                the most current version of these Terms at any time <span className="text-ophira-yellow"><a href="https://youaux.com/terms-and-conditions" target="_blank">here</a></span>. The revised Terms will become effective thirty (30) days after we
                communicate the revisions, and if you use the Services after that date, your continued use will be deemed acceptance of
                the revised Terms. If any change to these Terms is not acceptable to you, your only remedy is to stop using the Site and
                the Services (in accordance with the termination provisions of the YouAux Distribution Agreement and/ or other
                applicable terms and conditions).
              </p>

              <p className="text-white/50 mt-6">
                YouAux will make available to you the Platform, the Site and the Services, subject to and in accordance with these
                Terms, including the YouAux Privacy Policy, the YouAux Digital Distribution Services Agreement and any other legal
                terms & conditions as listed by YouAux on the Site.
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">1. DEFINITIONS</h2>

              <p className="text-white/50">For the purposes of these Terms:</p>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  <span className="font-medium">(a) Content</span> shall mean the album art, lyrics, graphics, fonts, photographs, written content, metadata, descriptions etc.
                  uploaded to the Platform by its Users, in accordance with these Terms.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(b) Distribution Plan</span> shall refer to the applicable distribution service plan chosen by the User.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(c) Platform</span> shall mean YouAux's proprietary platform and associated technology available on the Site,
                  made available by YouAux to you in accordance with these Terms, including the YouAux Privacy Policy, the YouAux
                  Distribution Agreement and any other legal terms & conditions as listed by YouAux on the Site.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(d) Services</span> shall mean the services rendered by YouAux, through its subsidiaries and collaborators, related to (i) the
                  distribution of the Songs/Videos, in accordance with the applicable Distribution Plan, including but not limited to the
                  placement of the Songs/Videos in authorised digital stores and streaming platforms; (ii) the collection of streaming
                  revenues and the payment of the percentages that correspond to the User, in accordance with its Distribution Plan, the
                  YouAux Distribution Agreement and these Terms; and (iii) marketing, promotional and analytical services in relation to the
                  aforementioned activities.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(e) Song(s)</span> shall mean the sound recording(s) (including the underlying works) which are uploaded to the Platform by Users
                  for distribution according to the applicable Distribution Plan.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(f) User(s)</span> shall mean you, the natural person or legal entity who uses or accesses the Site and/ or the Services, with a User
                  account or not.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(g) Video(s)</span> shall mean the audio-visual recording(s) (including the underlying works) which are uploaded to the Platform
                  by Users for distribution according to the applicable Distribution Plan.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">2. USE OF THE SITE, PLATFORM & SERVICES</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  <span className="font-medium">(a) Your use of the Site</span>, regardless of the plan you choose, constitutes a personal use license, which in no way authorizes
                  you or any third party authorised by you to any use other than as implied or intended by the Platform and the Services.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(b) The Services</span> are aimed at individuals over 18 years of age or any legal entity with the capacity to be bound by
                  applicable contract law, and shall be subject to contractual obligations and rights. If you are under 18 years of age, you
                  may use the Platform and Services only with the authorization of your parents or legal guardian.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(c) You are strictly prohibited</span> from engaging in or authorising any third party to engage in any of the following activities:
                </p>

                <div>
                  <p className="text-white/50 mb-3">
                    (i) Fix, record, produce, reproduce, store, distribute, communicate, make available, transport, or market the software on the
                    Site and/ or Platform, as well as any type of material contained therein, including sounds, names of artists, images,
                    graphics with and without animation, programming code, etc.
                  </p>
                  <p className="text-white/50 mb-3">
                    (ii) Use, in whole or in part, any type of content on the Site and/ or the Platform for commercial uses or public distribution,
                    for commercial and non-commercial purposes.
                  </p>
                  <p className="text-white/50 mb-3">
                    (iii) Reverse engineer or decompile all or part of any software contained on the Site and/ or the Platform;
                  </p>
                  <p className="text-white/50 mb-3">
                    (iv) Omit or remove any copyright as well as any other rights holder of the Songs/Videos;
                  </p>
                  <p className="text-white/50 mb-3">
                    (v) Transfer or offer to download any of material from the Platform on any unauthorised third-party server;
                  </p>
                  <p className="text-white/50 mb-3">
                    (vi) Manipulate or interfere with the operation of the Site and/ or the Platform, including but not limited to any fraudulent or
                    willfully negligent act or omission that impacts the Site, Platform and/ or the Services.
                  </p>
                </div>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(d) Any violation</span> of the provisions of these Terms, specifically this Clause, will result in the immediate and automatic
                  termination of your license to use the Site, the Services and the Platform. In such an instance, you shall destroy any type of
                  material from the Site and/or Platform that you may have downloaded. YouAux reserves its right to initiate suitable legal 
                  proceedings to enforce its rights under law.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(e) YouAux does not guarantee</span> that the content of the Site, including the Platform or any links contained in the Site, is
                  error-free, accurate, complete, or up-to-date. YouAux reserves the right to make changes to the Site and/ or the Platform
                  at any time without providing you or anyone else with any notice.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(f) YouTube Content ID revenue share.</span> YouAux will remit eighty-five percent (85%) of revenue generated from YouTube Content ID monetization to eligible Users, subject to applicable distribution agreements, platform policies, deductions, and payout schedules.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">3. OWNERSHIP & INTELLECTUAL PROPERTY</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  <span className="font-medium">(a) You acknowledge and agree that</span>, as between the parties, YouAux retains all right, title, and interest in and to the Site,
                  Platform and the Services, all copies or parts thereof (by whomever produced) and all intellectual property rights therein.
                  YouAux grants no, and reserves any and all, rights other than the rights expressly granted to you under these Terms with
                  respect to the Platform or the Services. You will not remove, obscure, or alter any intellectual property rights notices
                  relating to the Platform.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(b) The trademarks, service marks, and logos of YouAux</span> (the "Company Trademarks") used and displayed on this website
                  are trademarks or service marks of YouAux. Other company, product, and service names located on the website may be
                  trademarks or service marks owned by third-parties (the "Third-Party Trademarks", and, collectively with the Company
                  Trademarks, the "Trademarks"). Nothing on this website or in these Terms should be construed as granting, by implication,
                  estoppel, or otherwise, any license or right to use any Trademark displayed on this website without the prior written
                  consent of YouAux specific for each such use. The Trademarks may not be used to disparage YouAux or the applicable
                  third-party, YouAux's or third-party's products or services, or in any manner (using commercially reasonable judgment)
                  that may damage any goodwill in the Trademarks. Use of any Trademarks as part of a link to or from any website is
                  prohibited without YouAux's prior written consent. All goodwill generated from the use of any Company Trademark shall
                  inure to YouAux's benefit.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(c) Songs, Videos & Content.</span> YouAux receives the license for the distribution for the digital transmission of the Songs,
                  Videos and Content, pursuant to any User signing up for the Services.
                </p>

                <p className="text-white/50 mb-4">
                  By using the Services, you, the User, declare that you are the owner of the Songs/Videos and/ or have the necessary rights
                  and authority to license the Songs/Videos and Content as intended for the Services. The User is responsible for the
                  authenticity, legitimacy and veracity of the Songs/Videos, Content and information made available to YouAux. The User is
                  liable to indemnify YouAux and hold it harmless against any claim related to the Songs/Videos and/ or the Content that
                  the User makes available to YouAux, regardless of the nature of such claim.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">4. INTELLECTUAL PROPERTY VIOLATIONS</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  Any person who wishes to file a complaint because they consider themselves the owner of the copyright or related rights,
                  without prejudging their quality of ownership of rights with respect to the work(s) claimed, must include the following
                  information in the notice submitted to YouAux:
                </p>

                <div>
                  <p className="text-white/50 mb-3">
                    (i) Identify the infringing content in the applicable Song or Video
                  </p>
                  <p className="text-white/50 mb-3">
                    (ii) Name of the owner or authorised legal representative and contact details;
                  </p>
                  <p className="text-white/50 mb-3">
                    (iii) Details of the original content — identify the original content, provide details of date and location of first
                    publication and any other relevant information (e.g. copyright registration, if applicable).
                  </p>
                </div>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(b) YouAux may withdraw, disable or suspend</span> unilaterally and in good faith, access to the publication,
                  dissemination, public communication, and/or exhibition of Songs/Videos or Content, to prevent the violation of legal
                  provisions or obligations derived from contractual or legal relationships, taking in any case reasonable measures to notify
                  the person whose material is removed or disabled.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(c) YouAux reserves the right to terminate</span> User accounts of repeat offenders in relation to any intellectual property
                  infringement, using effective technological measures and/or algorithms at its disposal to detect such infringement.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(d) Any User who has their Content/ Song/ Video removed or suspended</span> under these Terms by YouAux may request for a
                  review of the decision by YouAux; provided the request for review is supported with sufficient material defending the
                  continued distribution or retention of the Content/ Song/ Video, as applicable. Regardless, YouAux is under no obligation
                  to reinstate or continue to distribute the Content/ Song/ Video.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(e) YouAux shall report any request for review</span> by a User to the complainant who reported such infringement, and unless
                  the complainant has initiated legal proceedings for such infringement, YouAux may, subject to the material submitted for
                  review, choose to reinstate or continue distribution of the Content/ Song/ Video in accordance with these Terms and the
                  YouAux Distribution Agreement.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(f) In the event that YouAux detects any misuse or malicious use</span> of the aforesaid procedure, it reserves the right to
                  ignore subsequent claims or notices from such Users.
                </p>

                <p className="text-white/50 mt-4">
                  <span className="font-medium">(h) AI-Generated Vocals Policy: </span>
                  The submission or distribution of songs containing AI-generated songs or AI-synthesized vocals is strictly prohibited on our platform. All vocals must be originally performed and recorded by human artists. Any content identified as containing AI-generated vocals will be subject to immediate rejection or removal without prior notice. Repeated violations may result in account suspension or permanent termination, in accordance with our Anti-Fraud and Content Authenticity Policies.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">5. USE OF DATA & PRIVACY</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  <span className="font-medium">(a) YouAux may:</span> (i) copy, use, modify, distribute, display, and disclose your data solely to the extent necessary to provide
                  the Services to you pursuant to the terms and conditions of these Terms; (ii) copy, modify, and use your data in connection
                  with internal operations and functions, including, but not limited to, operational analytics and reporting, internal financial
                  reporting and analysis, audit functions and archival purposes; and (iii) copy, modify and use your data on an aggregate and
                  de-identified basis, stripped of any personally identifiable information, for marketing purposes and internal business
                  purposes and analytics, provided that such use or disclosure does not identify you or your products, services, segments,
                  attributes, search criteria or consist of data solely attributable to you. For purposes hereof, "your data" means any and all
                  information collected and/or stored by or on behalf of you in connection with or derived from your use of the Platform,
                  excluding data and information relating to the operation and/or performance of the Platform. YouAux reserves the right
                  to access your account in order to respond to your requests for technical support. YouAux has the right, but not the
                  obligation, to monitor the Service, the Platform and all video, audio, image, text, software and any other content or data
                  available on the Platform or through the Service.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(b) YouAux reserves the right to use your name, image, likeness and/or company name</span> as a reference for marketing or
                  promotional purposes, including but not limited to on YouAux's website and in other communication with existing or
                  potential YouAux customers. Please write to support@youaux.com in the event you do not wish to be used as a reference.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(c) You may from time to time provide suggestions, comments for enhancements or functionality or other feedback</span>
                  ("Feedback") to YouAux with respect to the Platform and/ or the Services. YouAux shall have full discretion to
                  determine whether or not to proceed with the development of the requested enhancements, new features, or functionality.
                  You hereby grant YouAux a royalty-free, fully paid up, worldwide, transferable, sublicensable, irrevocable, perpetual
                  license to (a) copy, distribute, transmit, display, perform, and create derivative works of the Feedback; and (b) use the
                  Feedback and/or any subject matter thereof, including without limitation, the right to develop, manufacture, have
                  manufactured, market, promote, sell, have sold, offer for sale, have offered for sale, import, have imported, rent, provide,
                  and/or lease products or services which practice or embody or are configured for use in practicing, the Feedback and/or
                  any subject matter of the Feedback.
                </p>

                <p className="text-white/50 mb-4">
                  Please visit the <span className="text-ophira-yellow"><a href="https://youaux.com/privacy" target="_blank">Privacy Policy</a></span> to understand how YouAux collects and uses personal information.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">6. COPYRIGHT CONFLICT & COMPLIANCE POLICY</h2>

              <p className="text-white/50">
                In the event that our team receives a copyright conflict or ownership dispute from any DSP (Digital Service Provider) or rights holder, the concerned user will be contacted by our compliance team for clarification. The user will be provided with a reasonable timeframe to submit valid ownership proof or supporting documents.
              </p>

              <p className="text-white/50 mt-4">
                Failure to respond or provide the required documentation within the given timeframe will be considered a violation of our platform policies. In such cases, the user’s account may be temporarily or permanently suspended. Additionally, any previously released content associated with the account will not be taken down automatically, the user will be required to pay applicable takedown fees, as the violation constitutes misuse of the platform and an attempt to commit fraud or misrepresentation against the company.
              </p>

              <p className="text-white/50 mt-4">
                Our company reserves the right to take appropriate action, including legal measures, to protect its operations, time, and reputation from fraudulent activities
              </p>

              <p className="text-white/50 mt-4">
                If our team finds any suspicious activity during a manual audit or routine check, if you don’t provide the required documents within the given time then your account will be permanently suspended.
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">7. TAKEDOWN POLICY</h2>
              <p className="text-white/50">
              If a user wishes to request a takedown (removal) of a song from the YouAux platform, the following policies and procedures apply based on the user's selected distribution plan:

              <br /><br />

              <span className="font-medium">
                {isPreviousVersion ? "For Basic Plan Users:" : "For Basic/Plus Plan Users:"}
              </span><br />
              Users on the Basic Plan are subject to a takedown fee for each song removal request. YouAux imposes this fee to cover administrative, operational, and distribution costs associated with processing the takedown. The user must contact YouAux support to initiate the takedown process, at which point payment of the applicable takedown fee will be required before the request is processed. 

              <br /><br />

              <span className="font-medium">
                {isPreviousVersion ? "Exemption Clause for Basic Plan:" : "Exemption Clause for Basic/Plus Plan:"}
              </span><br />
              {isPreviousVersion
                ? "If a Basic Plan user believes they have a valid and compelling reason for requesting the takedown (such as copyright issues, accidental uploads, or extenuating circumstances), they are encouraged to reach out to YouAux support and provide a detailed explanation and supporting documentation. Upon review, YouAux reserves the right, at its sole discretion, to waive (exempt) the takedown fee for Basic Plan users if the provided reason is deemed justified and in accordance with platform policies."
                : "If a Basic/Plus Plan user believes they have a valid and compelling reason for requesting the takedown (such as copyright issues, accidental uploads, or extenuating circumstances), they are encouraged to reach out to YouAux support and provide a detailed explanation and supporting documentation. Upon review, YouAux reserves the right, at its sole discretion, to waive (exempt) the takedown fee for Basic/Plus Plan users if the provided reason is deemed justified and in accordance with platform policies."}

              <br /><br />

              <span className="font-medium">
                {isPreviousVersion ? "For Pro, Premium, and Premium+ Plan Users:" : "For Pro, Premium Plan Users:"}
              </span><br />
              {isPreviousVersion
                ? "Users subscribed to Pro, Premium, or Premium+ Plans are entitled to complimentary takedowns. No takedown fee will be charged for song removals requested under these plans. Users can contact the support team or utilize any self-service features (if available) to request song removal without incurring additional charges."
                : "Users subscribed to Pro, Premium Plans are entitled to complimentary takedowns. No takedown fee will be charged for song removals requested under these plans. Users can contact the support team or utilize any self-service features (if available) to request song removal without incurring additional charges."}

              <br /><br />

              <span className="font-medium">General Notes:</span><br />
              All takedown requests, regardless of the user's plan, must be submitted through official support channels and in compliance with the platform's guidelines and terms of service. YouAux may request additional information to verify the ownership and legitimacy of takedown requests. Failure to provide accurate or sufficient information may result in delays or denial of the takedown request.
              </p>


              <h2 className="text-2xl font-bold mt-10 mb-6">8. THIRD PARTY WEBSITES & SERVICES</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  <span className="font-medium">(a) The Site may contain links to third-party websites</span> ("External Sites"). These links are provided solely as a convenience to
                  Users and not as an endorsement by us of the content on such External Sites. The content of such External Sites is
                  developed and provided by others. Users should contact the site administrator or webmaster for those External Sites if
                  Users have any concerns regarding such links or any content located on such External Sites.
                </p>

                <p className="text-white/50 mb-4">
                  <span className="font-medium">(b) YouAux is not responsible for the content of any linked External Sites</span> and does not make any representations
                  regarding the content or accuracy of any materials on such External Sites. You are required to take necessary precautions
                  when downloading files from all websites to protect Users' computers from viruses and other destructive programs. If you
                  decide to access any External Sites, you are doing so at your own risk.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">9. REPRESENTATIONS AND WARRANTIES</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  You represent and warrant to YouAux that (i) you have full power and authority to enter into these Terms; (ii) your
                  activities in connection with the Site, Platform and/ or Services do not and will not violate, infringe, or misappropriate any
                  third party's copyright, trademark, right of privacy or publicity, or other personal or proprietary right, nor be defamatory,
                  obscene, unlawful, threatening, abusive, tortious, offensive or harassing; and (iii) you are eighteen (18) years of age or
                  older.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">10. DISCLAIMER OF WARRANTIES</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  The Site, the Platform, the Services, and all server and network components are all provided on an
                  "As Is" and "As Available" basis without any warranties of any kind, and YouAux expressly disclaims
                  Any and all warranties, whether express or implied, including, but not limited to, the implied
                  Warranties of Merchantability, Title, Fitness for a Particular Purpose, and Non-INFRINGEMENT. You
                  Acknowledge that YouAux does not warrant that the Services or the Platform will be
                  Uninterrupted, Timely, Secure, Error-Free or Virus-Free, NOR DOES IT MAKE ANY WARRANTY AS TO THE
                  Results, Including Revenue Generation, That May Be Obtained From Use Of The Service, And No
                  Information, Advice Or Services Obtained By You From YouAux Or Through The Service Shall Create
                  Any Warranty Not Expressly Stated In This Agreement.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">11. LIMITATION OF LIABILITY</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  To the extent permitted by law, in no event will YouAux be liable to you or any third party, under
                  contract, tort or otherwise, for (A) any indirect, incidental, special, exemplary, consequential, or punitive damages, including lost profits,
                  lost sales or business, or lost data; or (B) for any direct damages, costs, losses, or liabilities in excess of the rupee
                  equivalent of one hundred (USD $100). The provisions of this section allocate the risks under this agreement between the
                  parties, and the parties have relied on these limitations in determining whether to enter into this agreement.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">12. CANCELLATION AND REFUND</h2>

              <p className="text-white/50">
                At YouAux, we do not offer refunds for any transactions or purchases made on our Platform. However, you have the flexibility to cancel your account or subscription at any time. Even after cancellation, you will retain all your earnings and content submitted prior to cancellation.
              </p>

              <p className="text-white/50 mt-4">
                Please note that once a cancellation request is processed, you will no longer have access to certain premium features or services available under your plan, but your earnings and past content submissions will remain available to you. We encourage you to use any remaining services or content before finalizing the cancellation.
              </p>

              <p className="text-white/50 mt-4">
                If you need assistance with the cancellation process, you can contact us via the details provided in the “Contact Us” section of this Terms and Conditions.
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">13. SHIPMENT AND DELIVERY</h2>

              <p className="text-white/50">
                As YouAux is a digital service provider, shipping and delivery of physical goods are not applicable to our business. All content and services provided by YouAux are delivered digitally, with no requirement for physical shipping.
              </p>

              <p className="text-white/50 mt-4">
                Any purchases or transactions made on our platform will be completed through online channels, and the delivery of services or content will occur immediately or within the time frame mentioned on the service pages. If you require assistance or clarification regarding the digital delivery process, feel free to reach out to our customer support team.
              </p>

              <h2 className="text-2xl font-bold mt-10 mb-6">14. INDEMNIFICATION</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  You shall defend, indemnify, and hold harmless YouAux from and against any claims, actions or demands, including
                  without limitation reasonable legal and accounting fees, arising or resulting from your breach of these Terms, the Privacy
                  Policy, any of the Songs/Videos and/ or the Content, or your other access, contribution to, use or misuse of the Service
                  and/ or the Services. YouAux shall provide notice to you of any such claim, suit or demand. YouAux reserves the right
                  to assume the exclusive defense and control of any matter which is subject to indemnification under this section. In such
                  case, you agree to cooperate with any reasonable requests assisting YouAux's defense of such matter.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">16. ASSIGNMENT</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  You shall not assign these Terms without the prior written consent of YouAux, but YouAux may assign or transfer these
                  Terms, in whole or in part, without restriction.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">17. MISCELLANEOUS</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated
                  to the minimum extent necessary so that these Terms will otherwise remain in full force and effect and enforceable. The
                  failure of YouAux to exercise or enforce any right or provision of these Terms shall not be a waiver of that right. You
                  acknowledge that these Terms is a contract between you and YouAux, even though it is electronic and is not physically
                  signed by you and YouAux, and it governs your use of the Site, the Platform and/ or the Services. Both parties agree that
                  these Terms, is the complete and exclusive statement of the mutual understanding of the parties and supersedes and
                  cancels all previous written and oral agreements, communications and other understandings relating to the subject matter
                  of these Terms, and that all waivers and modifications must be in a writing signed by both parties, except as otherwise
                  provided herein. No agency, partnership, joint venture, or employment is created as a result of these Terms and you do not
                  have any authority of any kind to bind YouAux in any respect whatsoever. In any action or proceeding to enforce rights
                  under these Terms, the prevailing party will be entitled to recover costs and attorneys' fees. All notices under these Terms
                  will be in writing and will be deemed to have been duly given when received, if personally delivered; when receipt is
                  electronically confirmed, if transmitted by facsimile or email; the day after it is sent, if sent for next day delivery by
                  recognized overnight delivery service; and upon receipt, if sent by certified or registered mail, return receipt requested.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">18. GOVERNING LAW</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  These Terms shall be governed by the laws of India, without regard to the principles of conflicts of law. You hereby
                  expressly agree to submit to the exclusive jurisdiction of the courts located in Delhi, India, for the purpose of resolving
                  any dispute relating to your access to or use of the Service.
                </p>
              </div>

              <h2 className="text-2xl font-bold mt-10 mb-6">19. NOTICES</h2>

              <div className="mt-4">
                <p className="text-white/50 mb-4">
                  If you have any questions or comments about these Terms, the Platform or the Services or you wish to report any violations
                  or grievances, you can reach us at:
                </p>

                <p className="text-white/50 mb-4">
                  Mailing address: B1/639 A, Janakpuri, West Delhi, Delhi 110058, India
                </p>

                <p className="text-white/50 mb-4">
                  Email: <a href="mailto:support@youaux.com" className="text-ophira-yellow">support@youaux.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default Terms;
